// lib/phishing/preview-payload-tokens.ts
//
// Replaces SEND-TIME payload tokens with TEMPORARY preview visuals so the email
// editor / AI Studio preview shows "a QR code goes here" (and an attachment
// chip) instead of a literal `{{qrCode}}` string or an empty gap.
//
// The REAL artifact is per-recipient and generated by the backend at send time:
//   - {{qrCode}}        -> a unique tracked QR <img> per recipient
//   - {{qrCodeUrl}}     -> the raw data-URI used inside <img src="{{qrCodeUrl}}">
//   - {{attachmentBlock}} -> the synthesized attachment card
// These previews are placeholders ONLY — never the code actually sent.

/** Build a deterministic QR-looking SVG (a believable placeholder, not a
 *  scannable code). Three finder patterns + a fixed pseudo-data fill. */
function buildQrPlaceholderSvg(): string {
  const N = 21 // QR v1 module count
  const M = 8 // px per module
  const grid = N * M
  const quiet = M
  const total = grid + quiet * 2

  const inFinderZone = (x: number, y: number): boolean =>
    (x < 8 && y < 8) || (x >= N - 8 && y < 8) || (x < 8 && y >= N - 8)

  const finderOn = (x: number, y: number): boolean => {
    const ring = (bx: number, by: number): boolean => {
      const lx = x - bx
      const ly = y - by
      if (lx < 0 || lx > 6 || ly < 0 || ly > 6) return false
      const edge = lx === 0 || lx === 6 || ly === 0 || ly === 6
      const inner = lx >= 2 && lx <= 4 && ly >= 2 && ly <= 4
      return edge || inner
    }
    return ring(0, 0) || ring(N - 7, 0) || ring(0, N - 7)
  }

  let rects = ''
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      const on = inFinderZone(x, y)
        ? finderOn(x, y)
        : (x * 7 + y * 13 + x * y) % 3 === 0 // deterministic "data" modules
      if (on) {
        rects += `<rect x="${quiet + x * M}" y="${quiet + y * M}" width="${M}" height="${M}"/>`
      }
    }
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${total}" height="${total}" viewBox="0 0 ${total} ${total}">` +
    `<rect width="${total}" height="${total}" fill="#ffffff"/>` +
    `<g fill="#111111">${rects}</g>` +
    `</svg>`
  )
}

const QR_DATA_URI = `data:image/svg+xml,${encodeURIComponent(buildQrPlaceholderSvg())}`

const QR_BLOCK =
  `<div style="text-align:center;margin:24px 0;">` +
  `<img src="${QR_DATA_URI}" alt="QR code (preview)" width="160" height="160" style="display:block;margin:0 auto;border:0;" />` +
  `<div style="font-size:11px;color:#888;margin-top:6px;font-style:italic;">Sample QR — a unique code is generated for each recipient at send time</div>` +
  `</div>`

const ATTACHMENT_CHIP =
  `<div style="display:inline-flex;align-items:center;gap:8px;border:1px solid #d0d5dd;border-radius:8px;padding:8px 12px;margin:12px 0;background:#f9fafb;font-family:sans-serif;">` +
  `<span style="font-size:18px;line-height:1;">&#128206;</span>` +
  `<span style="font-size:13px;color:#344054;">attachment.pdf</span>` +
  `<span style="font-size:11px;color:#98a2b3;">&middot; generated at send</span>` +
  `</div>`

/** A neutral attachment name for a preview with no configured file name. */
const SAMPLE_ATTACHMENT_FILE_NAME = 'attachment.pdf'

export interface PreviewPayloadTokenOptions {
  /**
   * The template's configured attachment file name (attachmentConfig.fileName),
   * so body copy that says "Please review {{attachmentFileName}}" names the file
   * the preview's chip shows. Omitted / blank → a neutral sample, so the preview
   * never leaks the literal token (STG-762 — only the send path filled it).
   */
  attachmentFileName?: string | null
}

/**
 * #737 — the send path appends a QR to a QR Code template whose body carries
 * no QR token (injectQrBlockByPosition, end of body). Mirror that in previews
 * so the author sees the code the recipient will get.
 */
export function withSendTimeQrToken(html: string, payloadType: string): string {
  if (payloadType !== 'QR Code') return html
  if (/\{\{\s*qrCode(Url)?\s*\}\}|\[QR CODE PLACEHOLDER\]/i.test(html)) return html
  return `${html}{{qrCode}}`
}

/**
 * Substitute payload tokens with temporary preview visuals. Pure + synchronous,
 * safe to call inside a render path. Order matters: handle the legacy literal
 * first, then the canonical tokens.
 */
export function resolvePayloadPreviewTokens(
  html: string,
  options: PreviewPayloadTokenOptions = {},
): string {
  if (!html) return html
  let out = html
  // Legacy human-readable placeholder (older AI-Studio bodies).
  out = out.split('[QR CODE PLACEHOLDER]').join(QR_BLOCK)
  // Canonical full-block QR token.
  out = out.replace(/\{\{\s*qrCode\s*\}\}/gi, QR_BLOCK)
  // Raw data-URI variant (used inside <img src="{{qrCodeUrl}}">).
  out = out.replace(/\{\{\s*qrCodeUrl\s*\}\}/gi, QR_DATA_URI)
  // Attachment chip token.
  out = out.replace(/\{\{\s*attachmentBlock\s*\}\}/gi, ATTACHMENT_CHIP)
  // Attachment FILE NAME token — body copy that names the attachment ("Please
  // review {{attachmentFileName}}"). The send path fills this from the delivered
  // file; the preview uses the configured name, else a neutral sample.
  const attachmentFileName =
    options.attachmentFileName?.trim() || SAMPLE_ATTACHMENT_FILE_NAME
  out = out.replace(/\{\{\s*attachmentFileName\s*\}\}/gi, attachmentFileName)
  return out
}
